import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import allImgPaths from "assets/images";

type FigureEntry = {
  figure: string;
  imageUrl?: string;
  legendItems: { number: string; text: string }[];
};

type ProfileEntry = { name: string; text: string };

const ClaimElement = ({ claim, type = null, patentDict = undefined }: any) => {
  // Clicking a feature opens its figure(s) — image + legend, same manner as
  // the Claim Figures panel — plus the analyzer-built element profile.
  const [modal, setModal] = useState<{
    hex?: string;
    description: string;
    entries: FigureEntry[];
    profiles: ProfileEntry[];
  } | null>(null);

  // Floating figure preview for the hovered feature — same manner as the
  // claim chart's sub-feature hover preview; clicking opens the full modal.
  const [hoverPreview, setHoverPreview] = useState<{
    imageUrl: string;
    description: string;
    hex?: string;
    figure?: string;
    left: number;
    top: number;
    above: boolean;
  } | null>(null);

  useEffect(() => {
    const clear = () => setHoverPreview(null);
    window.addEventListener("scroll", clear, true);
    return () => window.removeEventListener("scroll", clear, true);
  }, []);

  useEffect(() => {
    if (!modal) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setModal(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modal]);

  // Same lookup order as ClaimGraphics' transformLegend: claim-level legends,
  // then element-level, plus the feature's own legends when present.
  const legendItemsFor = (
    figure: string,
    element?: any,
    feature?: any
  ): { number: string; text: string }[] => {
    const pools = [claim?.legends, feature?.legends, element?.legends].filter(
      (p) => Array.isArray(p)
    );
    for (const pool of pools) {
      const found = pool.find(
        (legend: any) =>
          legend && Object.prototype.hasOwnProperty.call(legend, figure)
      );
      if (found && Array.isArray(found[figure])) {
        return found[figure].map((item: any) => {
          const key = Object.keys(item)[0];
          return { number: key, text: item[key] };
        });
      }
    }
    return [];
  };

  // holder = feature (new structure) or element (current structure); presigned
  // URLs live on the holder, with element/claim level as fallbacks.
  const figureEntriesFor = (holder: any, element?: any): FigureEntry[] =>
    (holder?.figures || [])
      .map((figure: string) => ({
        figure,
        imageUrl:
          holder?.figurePreSignedUrls?.[figure] ||
          element?.figurePreSignedUrls?.[figure] ||
          claim?.figurePreSignedUrls?.[figure],
        legendItems: legendItemsFor(figure, element, holder),
      }))
      .filter((e: FigureEntry) => !!e.imageUrl);

  // Parts often carry empty figures arrays while the figure itself sits one
  // level up — fall back feature → element → claim, but only for parts that
  // name a claim part ("system (100)"), so glue words ("A ", "comprising")
  // don't all open the claim-level figure.
  const entriesWithFallback = (holder: any, element?: any): FigureEntry[] => {
    let entries = figureEntriesFor(holder, element);
    if (
      entries.length === 0 &&
      /\(\s*\d+\s*\)/.test(String(holder?.description || ""))
    ) {
      if (element) entries = figureEntriesFor(element);
      if (entries.length === 0) entries = figureEntriesFor(claim);
    }
    return entries;
  };

  // patent_dict keys are normalized feature names ("handheld_device140",
  // "grip130"); match every key the clicked text mentions, keeping only the
  // most specific when one key's name is contained in another's
  // ("handheld_device" loses to "handheld_device140").
  const profilesFor = (description: string): ProfileEntry[] => {
    if (!patentDict || typeof patentDict !== "object") return [];
    const norm = (s: string) =>
      String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const normDesc = norm(description);
    if (!normDesc) return [];
    const matched = Object.entries(patentDict)
      .filter(
        ([key, value]) =>
          typeof value === "string" &&
          value.trim() &&
          norm(key) &&
          normDesc.includes(norm(key))
      )
      .sort(([a], [b]) => norm(b).length - norm(a).length);
    const kept: [string, any][] = [];
    matched.forEach(([key, value]) => {
      if (!kept.some(([k]) => norm(k).includes(norm(key))))
        kept.push([key, value]);
    });
    return kept.map(([key, value]) => ({
      name: key.replace(/_/g, " "),
      text: String(value).trim().replace(/^"|"$/g, ""),
    }));
  };

  const openFigures = (holder: any, entries: FigureEntry[]) => {
    const description = String(holder?.description || "");
    const profiles = profilesFor(description);
    if (entries.length === 0 && profiles.length === 0) return;
    setModal({
      hex: holder?.hex_code,
      description,
      entries,
      profiles,
    });
  };

  // Feature/element/preamble span: clickable (zoom-in cursor + dotted
  // underline on hover) only when it actually has a figure to show. Hover
  // shows a floating figure preview; click expands to the full modal.
  const claimSpan = (holder: any, element: any, key: number) => {
    const entries = entriesWithFallback(holder, element);
    const clickable =
      entries.length > 0 ||
      profilesFor(String(holder?.description || "")).length > 0;
    const showPreview = (ev: React.MouseEvent) => {
      const withImage = entries.find((e) => !!e.imageUrl);
      if (!withImage) return;
      const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
      const half = 140; // popup is 280px wide — keep it inside the viewport
      const left = Math.min(
        Math.max(rect.left + rect.width / 2, half + 8),
        window.innerWidth - half - 8
      );
      const above = rect.bottom + 300 > window.innerHeight;
      const top = above ? rect.top - 8 : rect.bottom + 8;
      setHoverPreview({
        imageUrl: withImage.imageUrl!,
        description: String(holder?.description || ""),
        hex: holder?.hex_code,
        figure: withImage.figure,
        left,
        top,
        above,
      });
    };
    return (
      <span
        key={key}
        style={{
          color: holder?.hex_code,
          ...(clickable ? { cursor: "zoom-in" } : {}),
        }}
        className={
          clickable
            ? "hover:underline decoration-dotted underline-offset-2"
            : undefined
        }
        title={clickable ? "Click to view figure & profile" : undefined}
        onMouseEnter={clickable ? showPreview : undefined}
        onMouseLeave={clickable ? () => setHoverPreview(null) : undefined}
        onClick={
          clickable
            ? () => {
                setHoverPreview(null);
                openFigures(holder, entries);
              }
            : undefined
        }
      >
        {holder?.description}
      </span>
    );
  };

  return (
    <div className="">
      {type === "vijilize" && (
        // Fixed height matching the Covered Patents header so the bottom
        // borders of both column headers line up.
        <div className="px-4 border-b border-[#CED2DA] h-[66px] flex flex-col justify-center">
          <p className="text-base font-interSemiBold text-[#0A090B]">
            Claim Features
          </p>
        </div>
      )}

      <div className="px-5 py-4">
        {
          Array.isArray(claim?.claim_preamble) || Array.isArray(claim?.preamble) ? (
            <div>
              {(Array.isArray(claim?.claim_preamble) ? claim.claim_preamble : claim.preamble).map((cp: any, cpIndex: number) => (
                <span key={cpIndex}>
                  {claimSpan(cp, undefined, cpIndex)}{' '}
                </span>
              ))}
            </div>
          ) : (
            <div>
              {claimSpan(
                {
                  description: claim?.claim_preamble || claim?.preamble,
                  hex_code: claim?.hex_code,
                  figures: claim?.figures,
                  figurePreSignedUrls: claim?.figurePreSignedUrls,
                },
                undefined,
                0
              )}
            </div>
          )
        }

        {
          claim.elements &&
          claim.elements.map((element: any, elemIndex: number) => (
            <div key={elemIndex} className="ml-7">
              {element?.features?.length > 0 ? (
                // New structure: render features array on same line
                element?.features?.map((feature: any, featureIndex: number) =>
                  claimSpan(feature, element, featureIndex)
                )
              ) : (
                // Current structure: render element description directly
                claimSpan(element, undefined, elemIndex)
              )}
            </div>
          ))}
      </div>

      {/* Floating figure preview for the hovered feature */}
      {hoverPreview &&
        createPortal(
          <div
            className="fixed pointer-events-none"
            style={{
              left: hoverPreview.left,
              top: hoverPreview.top,
              transform: `translate(-50%, ${
                hoverPreview.above ? "-100%" : "0"
              })`,
              zIndex: 1300,
            }}
          >
            <div className="w-[280px] bg-white border border-gray-200 rounded-lg shadow-xl p-2">
              <img
                src={hoverPreview.imageUrl}
                alt={hoverPreview.description}
                className="w-full max-h-[220px] object-contain bg-[#FAFBFC] rounded"
              />
              <p className="mt-1.5 text-center text-[11px] font-interSemiBold truncate">
                <span
                  style={
                    hoverPreview.hex ? { color: hoverPreview.hex } : undefined
                  }
                >
                  {hoverPreview.description}
                </span>
                {hoverPreview.figure && (
                  <span className="text-[#667085] font-interMedium">
                    {" · "}
                    Fig. {hoverPreview.figure.replace("FIG_", "")}
                  </span>
                )}
              </p>
            </div>
          </div>,
          document.body
        )}

      {/* Figure modal for the clicked feature */}
      {modal &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 1250 }}
            onClick={() => setModal(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
              style={{ width: "62vw", maxWidth: "900px", maxHeight: "84vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-4 px-4 py-2.5 border-b border-gray-200">
                <span
                  className="text-sm font-interSemiBold truncate"
                  style={modal.hex ? { color: modal.hex } : undefined}
                  title={modal.description}
                >
                  {modal.description}
                </span>
                <button
                  type="button"
                  title="Close"
                  onClick={() => setModal(null)}
                  className="p-1 rounded hover:bg-gray-100 text-[#667085] flex-shrink-0"
                  style={{ cursor: "pointer" }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-8">
                {modal.entries.map(({ figure, imageUrl, legendItems }) => (
                  <div key={figure} className="w-full">
                    <div className="flex gap-x-8">
                      <div className="w-1/2 flex justify-center">
                        <img
                          src={imageUrl}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src =
                              allImgPaths.claimFigureNotFound;
                          }}
                          alt={`Patent figure ${figure}`}
                          className="w-full max-w-sm object-contain"
                        />
                      </div>
                      <div className="w-1/2">
                        <ul className="space-y-2">
                          {legendItems.map((item) => (
                            <li
                              key={item.number}
                              className="text-sm text-gray-900 font-interRegular"
                            >
                              {item.number}. {item.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 bg-[#F9FAFB] py-2 px-3 text-center w-11/12 mx-auto">
                      <span className="text-sm text-[#272E35] font-interMedium">
                        Fig. {figure.replace("FIG_", "")}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Analyzer-built element profile(s) for the clicked feature */}
                {modal.profiles.length > 0 && (
                  <div className="border border-gray-200 rounded-lg bg-[#F8FAFC] px-4 py-3">
                    <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-2">
                      Element Profile
                    </p>
                    <div className="space-y-3">
                      {modal.profiles.map((profile) => (
                        <div key={profile.name}>
                          <p className="text-sm font-interSemiBold text-[#1A202C] capitalize">
                            {profile.name}
                          </p>
                          <p className="text-sm text-[#475569] leading-relaxed">
                            {profile.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ClaimElement;
